import { cn } from '@/lib/utils';

// =====================================================================
// Inspector panel shell — the right-anchored side panel that opens
// when a complex block is selected on the canvas.
//
// Layout (see docs/design-tokens-3b-3d.md §2.1):
// - ≥1280px: docked aside, pushes canvas inward via the parent flex
//   row in PageEditLoaded.
// - 768–1279px: overlay anchored to the viewport's right edge with
//   shadow-lg.
// - <768px: not rendered. The dashboard editor itself does not run
//   on mobile (MobileFallback in page-edit.tsx shows a switch-to-
//   laptop message), so the panel inherits that constraint.
//
// Close handling lives in the parent (Esc + click-outside +  the
// header's ✕ button), so this component is purely presentational.
// =====================================================================

interface InspectorPanelProps {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
}

export function InspectorPanel({ open, children }: InspectorPanelProps) {
  if (!open) return null;

  return (
    <>
      {/* Narrow desktop floating overlay (768–1279px) */}
      <aside
        className={cn(
          'hidden md:flex md:flex-col xl:hidden',
          'fixed right-0 top-0 z-30',
          'h-dvh',
          'w-[var(--inspector-width)]',
          'border-l border-border bg-card shadow-lg',
        )}
        role="complementary"
        aria-label="Block properties"
      >
        {children}
      </aside>

      {/* Wide desktop docked panel (≥1280px) — sits next to canvas via
          parent flex layout. */}
      <aside
        className={cn(
          'hidden xl:flex xl:flex-col',
          'w-[var(--inspector-width)] shrink-0',
          'sticky top-0 h-dvh',
          'border-l border-border bg-card',
        )}
        role="complementary"
        aria-label="Block properties"
      >
        {children}
      </aside>
    </>
  );
}
